import React, { useRef } from "react";
import { Box, IconButton } from "@mui/material";
import { useTheme, type Theme } from "@mui/material/styles";
import { DownloadSimpleIcon, PrinterIcon } from "@phosphor-icons/react";
import { useGlobalToast } from "components/GlobalToastProvider";
import { downloadDocument } from "./downloadDocument";

interface DocumentFileActionsProps {
  documentUri?: string | null;
  filename?: string;
}

export default function DocumentFileActions({ documentUri, filename }: DocumentFileActionsProps) {
  const printerRef = useRef<HTMLIFrameElement>(null);
  const theme = useTheme<Theme & { radius: { large: string } }>();
  const { showToast } = useGlobalToast();

  function handlePrint() {
    const printer = printerRef.current?.contentWindow;
    if (!printer) {
      showToast({ message: "Unable to print this document. Please try again.", errorState: true });
      return;
    }
    printer.focus();
    printer.print();
  }

  return (
    <Box
      sx={{
        display: "flex",
        p: 1,
        justifyContent: "center",
        alignItems: "center",
        gap: 1,
        borderRadius: theme.radius.large,
        background: theme.palette.divider,
      }}
    >
      <iframe
        ref={printerRef}
        title="Document print preview"
        style={{ display: "none" }}
        src={documentUri ?? undefined}
      />
      <IconButton
        color="secondary"
        size="small"
        aria-label="Print document"
        disabled={!documentUri}
        onClick={handlePrint}
      >
        <PrinterIcon weight="duotone" />
      </IconButton>
      <IconButton
        color="secondary"
        size="small"
        aria-label="Download document"
        disabled={!documentUri}
        onClick={() => downloadDocument(documentUri, filename)}
      >
        <DownloadSimpleIcon weight="duotone" />
      </IconButton>
    </Box>
  );
}
